/* TransferLabs — Rush / Jump-the-line cart toggle
 *
 * Uses document-level event delegation so the toggle keeps working
 * after the cart morphs (which would otherwise wipe an inline listener).
 *
 * - Adding:   POST /cart/add.js     (idempotent against double-clicks via the
 *                                    busy flag; we pre-check qty so a flip
 *                                    on/off/on never produces qty 2)
 * - Removing: POST /cart/change.js  (zero-out the line item by variant id)
 *
 * Companion snippet: snippets/tl-rush-toggle.liquid
 */
(function () {
  'use strict';

  if (window.__tlRushToggleInit) return;
  window.__tlRushToggleInit = true;

  var ROUTES = (window.Theme && window.Theme.routes) || {};
  var CART_ADD_URL    = ROUTES.cart_add_url    || '/cart/add.js';
  var CART_CHANGE_URL = ROUTES.cart_change_url || '/cart/change.js';
  var CART_JS_URL     = ROUTES.cart_url        || '/cart.js';

  var SOURCE_ID = 'tl-rush-toggle';
  var ERROR_HIDE_MS = 5000;

  function collectSectionIds() {
    var ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    document.querySelectorAll('cart-icon-component[data-section-id]').forEach(function (el) {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  function showError(root, message) {
    var errorEl = root.querySelector('[data-tl-rush-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    setTimeout(function () { errorEl.hidden = true; }, ERROR_HIDE_MS);
  }

  function dispatchCartUpdate(data, variantId) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: data,
        sourceId: SOURCE_ID,
        data: {
          source: SOURCE_ID,
          sections: data && data.sections,
          itemCount: data && data.item_count,
          variantId: String(variantId),
        },
      },
    }));
  }

  async function postJSON(url, payload) {
    var res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    var data = null;
    try { data = await res.json(); } catch (e) { /* may be empty */ }
    if (!res.ok || (data && data.errors)) {
      var msg = data && (typeof data.errors === 'string' ? data.errors : data.message);
      throw new Error(msg || 'Cart request failed.');
    }
    return data;
  }

  // Read the cart so we know our current qty for the rush variant.
  // Any failure → assume 0 so we still attempt the user's intent.
  async function currentQtyForVariant(variantId) {
    try {
      var res = await fetch(CART_JS_URL, { headers: { Accept: 'application/json' } });
      if (!res.ok) return 0;
      var cart = await res.json();
      var items = (cart && cart.items) || [];
      return items.reduce(function (sum, item) {
        return item && item.variant_id === variantId ? sum + (item.quantity || 0) : sum;
      }, 0);
    } catch (e) { return 0; }
  }

  // Final hop: refresh sections so the cart drawer / totals re-render with
  // the new state. /cart.js doesn't return rendered sections, so we hit
  // /cart/update.js as a no-op with sections=… to get them.
  async function refreshSections() {
    var sectionIds = collectSectionIds();
    var body = { updates: {}, sections_url: window.location.pathname };
    if (sectionIds.length) body.sections = sectionIds.join(',');
    return postJSON('/cart/update.js', body);
  }

  async function handleToggle(input) {
    var root = input.closest('[data-tl-rush-toggle]');
    if (!root) return;

    if (root.dataset.tlBusy === '1') {
      input.checked = !input.checked;
      return;
    }

    var variantId = parseInt(root.dataset.variantId, 10);
    if (!variantId) {
      showError(root, 'Rush product is misconfigured. Please refresh and try again.');
      input.checked = !input.checked;
      return;
    }

    var wantOn = input.checked;

    root.dataset.tlBusy = '1';
    root.classList.add('is-busy');
    root.classList.toggle('is-on', wantOn);

    try {
      var existingQty = await currentQtyForVariant(variantId);

      // Shopify's /cart/change.js validates `id` against a string regex
      // (variant id digits OR line-item key). Sending a JS Number throws
      // "expected Integer to be a …" — always coerce to String.
      var idStr = String(variantId);

      if (wantOn) {
        if (existingQty <= 0) {
          await postJSON(CART_ADD_URL, { id: idStr, quantity: 1 });
        }
        // already in cart → no-op
      } else {
        if (existingQty > 0) {
          await postJSON(CART_CHANGE_URL, { id: idStr, quantity: 0 });
        }
        // not in cart → no-op
      }

      var sectionsResp = await refreshSections();
      dispatchCartUpdate(sectionsResp, variantId);
    } catch (err) {
      console.error('[tl-rush-toggle]', err);
      input.checked = !wantOn;
      root.classList.toggle('is-on', !wantOn);
      showError(root, err.message || 'Could not update the cart. Please try again.');
    } finally {
      root.dataset.tlBusy = '';
      root.classList.remove('is-busy');
    }
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-tl-rush-input]')) return;
    handleToggle(target);
  });
})();
